/**
 * Configuración de i18next de la CMDB.
 *
 * Idiomas incluidos en el build: es, ca, en (`src/i18n/*.json`). Además, el
 * backend puede servir catálogos adicionales desde la base de datos: se cargan
 * en caliente con `ensureLanguageResources()` y se funden con el JSON local.
 *
 * El idioma inicial se decide ANTES de renderizar (igual que el aspecto), para
 * que la pantalla de bienvenida ya salga en el idioma del navegador:
 * elección guardada → `navigator.language` → español.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import es from './es.json';
import ca from './ca.json';
import en from './en.json';
import {
  initialLanguage,
  normalizeLanguageCode,
  rememberLanguage,
} from './languages';

export const BUNDLED_LANGUAGES = { es, ca, en };

const API_ROOT = (
  process.env.REACT_APP_API_URL ??
  window.__ENV__?.API_URL ??
  ''
).replace(/\/$/, '');

const loadedRemoteLanguages = new Set();

i18n.use(initReactI18next).init({
  resources: {
    es: { translation: es },
    ca: { translation: ca },
    en: { translation: en },
  },
  lng: initialLanguage(),
  fallbackLng: 'es',
  interpolation: {
    escapeValue: false,
  },
  // Un valor ausente se devuelve como cadena vacía en vez de `null`: evita
  // errores de render en listas y deja el fallback de español funcionar.
  returnNull: false,
});

if (typeof document !== 'undefined') {
  document.documentElement.lang = i18n.language || 'es';
}

function authHeaders() {
  try {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/** Convierte `{server: {form: {name: 'x'}}}` en `{'server.form.name': 'x'}`. */
function flattenResource(value, prefix = '', out = {}) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    for (const [key, child] of Object.entries(value)) {
      flattenResource(child, prefix ? `${prefix}.${key}` : key, out);
    }
  } else if (typeof value === 'string') {
    out[prefix] = value;
  }
  return out;
}

/** Vuelve a anidar las claves planas que devuelve `/api/i18n/bundle`. */
function unflattenResource(flat) {
  const root = {};
  for (const [key, value] of Object.entries(flat || {})) {
    if (typeof value !== 'string') continue;
    const parts = key.split('.');
    let node = root;
    for (let index = 0; index < parts.length - 1; index += 1) {
      const part = parts[index];
      if (!node[part] || typeof node[part] !== 'object') node[part] = {};
      node = node[part];
    }
    node[parts[parts.length - 1]] = value;
  }
  return root;
}

function mergeResource(base, extra) {
  if (!base || typeof base !== 'object') return extra;
  if (!extra || typeof extra !== 'object') return extra;
  const merged = { ...base };
  for (const [key, value] of Object.entries(extra)) {
    const current = merged[key];
    if (
      current &&
      typeof current === 'object' &&
      value &&
      typeof value === 'object' &&
      !Array.isArray(current) &&
      !Array.isArray(value)
    ) {
      merged[key] = mergeResource(current, value);
    } else {
      merged[key] = value;
    }
  }
  return merged;
}

/**
 * Descarga el catálogo de un idioma desde el backend y lo funde con el del
 * build. Si el endpoint no está disponible (backend antiguo o sin migrar), se
 * mantiene el JSON local: la app nunca se queda sin textos.
 */
export async function loadLanguageBundle(code) {
  const language = normalizeLanguageCode(code);
  if (!language || loadedRemoteLanguages.has(language)) return false;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(`${API_ROOT}/api/i18n/bundle/${language}`, {
      headers: { ...authHeaders() },
      credentials: 'include',
      signal: controller.signal,
    });
    if (!response.ok) return false;
    const payload = await response.json();
    const entries = payload?.data?.entries || {};
    if (!Object.keys(entries).length) {
      loadedRemoteLanguages.add(language);
      return false;
    }

    const current = i18n.getResourceBundle(language, 'translation') || {};
    const merged = mergeResource(current, unflattenResource(entries));
    i18n.addResourceBundle(language, 'translation', merged, true, true);
    loadedRemoteLanguages.add(language);
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/** Idiomas activos según el servidor (incluye los añadidos desde el panel). */
export async function fetchRemoteLanguages() {
  try {
    const response = await fetch(`${API_ROOT}/api/i18n/languages`, {
      headers: { ...authHeaders() },
      credentials: 'include',
    });
    if (!response.ok) return [];
    const payload = await response.json();
    return payload?.data?.languages || [];
  } catch {
    return [];
  }
}

/**
 * Cambia el idioma de la interfaz. Si el catálogo no viene en el build, lo
 * descarga antes de cambiar para no enseñar claves crudas ni español a medias.
 */
export async function changeLanguage(code, { persist = true } = {}) {
  const language = normalizeLanguageCode(code) || 'es';
  await loadLanguageBundle(language);
  await i18n.changeLanguage(language);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language;
  }
  if (persist) rememberLanguage(language);
  return language;
}

export function currentLanguage() {
  return normalizeLanguageCode(i18n.language) || 'es';
}

export { i18n, flattenResource };
export default i18n;
